import React, { useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import Api from 'utils/api'
import ButtonGradient from 'component/Buttons/dashboard-btn'

const COLOR_FIELDS = [
  { key: 'page_bg_color',              label: 'Page Background' },
  { key: 'sidenav_bg_color',           label: 'Sidebar Background' },
  { key: 'sidenav_text_color',         label: 'Sidebar Text' },
  { key: 'sidenav_button_bg_color',    label: 'Sidebar Button Background' },
  { key: 'sidenav_button_text_color',  label: 'Sidebar Button Text' },
  { key: 'button_bg_color',            label: 'Button Background' },
  { key: 'button_text_color',          label: 'Button Text' },
]

function ColorInput({ label, value, onChange }) {
  const [open, setOpen] = useState(false)
  const hex = value || '#ffffff'

  return (
    <div className='relative'>
      <label className='block text-xs text-gray-500 mb-1'>{label}</label>
      <div
        onClick={() => setOpen(o => !o)}
        className='flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:border-gray-300'
      >
        <div className='w-6 h-6 rounded border border-gray-200' style={{ backgroundColor: hex }} />
        <span className='text-sm font-mono text-gray-700'>{hex}</span>
      </div>
      {open && (
        <div className='absolute z-20 mt-1 p-2 bg-white rounded-xl shadow-xl border border-gray-100'>
          <HexColorPicker color={hex} onChange={onChange} />
          <input
            type='text'
            value={hex}
            onChange={e => onChange(e.target.value)}
            className='mt-2 w-full border border-gray-200 rounded px-2 py-1 text-xs font-mono'
          />
          <button onClick={() => setOpen(false)} className='mt-1 w-full text-xs text-gray-400 hover:text-gray-600'>Done</button>
        </div>
      )}
    </div>
  )
}

function MerchantBranding({ location }) {
  const existing = location?.location_config || {}
  const [colors, setColors] = useState(
    Object.fromEntries(COLOR_FIELDS.map(f => [f.key, existing[f.key] || '']))
  )
  const [logoUrl, setLogoUrl]   = useState(existing.logo || '')
  const [coverUrl, setCoverUrl] = useState(existing.cover || '')
  const [uploading, setUploading] = useState({})
  const [saving, setSaving] = useState(false)

  const uploadImage = async (file, field) => {
    const form = new FormData()
    form.append('image', file, file.name)
    setUploading(u => ({ ...u, [field]: true }))
    const { response } = await Api('/utils/upload/image?desiredPath=branding', 'post', { payload: form })
    setUploading(u => ({ ...u, [field]: false }))
    if (response?.data) {
      if (field === 'logo')  setLogoUrl(response.data)
      if (field === 'cover') setCoverUrl(response.data)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    await Api('/location/branding/' + location._id, 'put', {
      payload: {
        location_config: { ...colors, logo: logoUrl, cover: coverUrl },
      },
      notify: true,
    })
    setSaving(false)
  }

  return (
    <div className='max-w-2xl space-y-6'>
      {/* Colors */}
      <div className='bg-white rounded-2xl shadow-sm p-6'>
        <h2 className='text-sm font-semibold text-gray-700 mb-4'>Theme Colors</h2>
        <div className='grid grid-cols-2 gap-4'>
          {COLOR_FIELDS.map(f => (
            <ColorInput
              key={f.key}
              label={f.label}
              value={colors[f.key]}
              onChange={val => setColors(c => ({ ...c, [f.key]: val }))}
            />
          ))}
        </div>
      </div>

      {/* Images */}
      <div className='bg-white rounded-2xl shadow-sm p-6'>
        <h2 className='text-sm font-semibold text-gray-700 mb-4'>Brand Images</h2>
        <div className='grid grid-cols-2 gap-6'>
          {[
            { field: 'logo',  label: 'Logo',  url: logoUrl },
            { field: 'cover', label: 'Cover / Background', url: coverUrl },
          ].map(({ field, label, url }) => (
            <div key={field}>
              <label className='block text-xs text-gray-500 mb-2'>{label}</label>
              {url && <img src={url} alt={label} className='w-full h-24 object-cover rounded-lg mb-2 border border-gray-100' />}
              <label className='block w-full text-center text-sm border border-dashed border-gray-300 rounded-lg py-3 cursor-pointer hover:border-gray-400 text-gray-500'>
                {uploading[field] ? 'Uploading…' : 'Upload image'}
                <input
                  type='file'
                  accept='image/*'
                  className='hidden'
                  onChange={e => e.target.files[0] && uploadImage(e.target.files[0], field)}
                />
              </label>
            </div>
          ))}
        </div>
      </div>

      <ButtonGradient type='button' onClick={handleSave} processing={saving}>
        Save Branding
      </ButtonGradient>
    </div>
  )
}

export default MerchantBranding
